import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import Breadcrumb from '../Breadcrumb';

interface EvaluationDetailsProps {}

interface EvaluationResult {
  data_point_id: string;
  input: any;
  output: any;
  target_label: string;
  pred_label: string;
  status: string;
}

const EvaluationDetails: React.FC<EvaluationDetailsProps> = () => {
  const { projectId, evalRunId } = useParams<{ projectId: string; evalRunId: string }>();
  const [results, setResults] = useState<EvaluationResult[]>([]);
  const [allResults, setAllResults] = useState<EvaluationResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [prevTokens, setPrevTokens] = useState<string[]>([]);
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const safeRender = (value: any): string => {
    if (value === null || value === undefined) return '<EMPTY>';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  useEffect(() => {
    if (projectId && evalRunId) {
      fetchAllResults();
      fetchResults();
    }
  }, [projectId, evalRunId]);

  useEffect(() => {
    if (projectId && evalRunId && currentPage > 1) {
      fetchResults();
    }
  }, [currentPage]);

  useEffect(() => {
    // Re-render charts when results change
  }, [results]);

  const fetchAllResults = async () => {
    try {
      const session = await fetchAuthSession();
      const authToken = session.tokens?.idToken?.toString();
      
      let allData: EvaluationResult[] = [];
      let nextToken: string | null = null;
      
      do {
        let url = `${process.env.REACT_APP_API_URL}/projects/${projectId}/evaluation/runs/${evalRunId}/results`;
        if (nextToken) {
          url += `?nextToken=${encodeURIComponent(nextToken)}`;
        }
        
        const response = await fetch(url, {
          headers: {
            'Authorization': authToken || ''
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          allData = [...allData, ...(data.results || [])];
          nextToken = data.nextToken || null;
        } else {
          break;
        }
      } while (nextToken);
      
      setAllResults(allData);
    } catch (error) {
      console.error('Error fetching all evaluation results:', error);
    }
  };

  const fetchResults = async (token?: string) => {
    try {
      setLoading(true);
      const session = await fetchAuthSession();
      const authToken = session.tokens?.idToken?.toString();
      
      let url = `${process.env.REACT_APP_API_URL}/projects/${projectId}/evaluation/runs/${evalRunId}/results`;
      if (token) {
        url += `?nextToken=${encodeURIComponent(token)}`;
      }
      
      const response = await fetch(url, {
        headers: {
          'Authorization': authToken || ''
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const sortedResults = sortResults(data.results || []);
        setResults(sortedResults);
        setHasNextPage(!!data.nextToken);
        setNextToken(data.nextToken || null);
      }
    } catch (error) {
      console.error('Error fetching evaluation results:', error);
    }
    setLoading(false);
  };

  const handleNextPage = () => {
    if (hasNextPage && nextToken) {
      setPrevTokens([...prevTokens, nextToken]);
      setCurrentPage(currentPage + 1);
      fetchResults(nextToken);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      const newPrevTokens = [...prevTokens];
      const prevToken = newPrevTokens.pop();
      setPrevTokens(newPrevTokens);
      setCurrentPage(currentPage - 1);
      fetchResults(prevToken);
    }
  };

  const handleSort = (field: string) => {
    const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(newDirection);
    const sortedResults = sortResults(results, field, newDirection);
    setResults(sortedResults);
  };

  const sortResults = (data: EvaluationResult[], field?: string, direction?: 'asc' | 'desc') => {
    if (!field && !sortField) return data;
    const sortBy = field || sortField;
    const sortDir = direction || sortDirection;
    
    return [...data].sort((a, b) => {
      let aVal = a[sortBy as keyof EvaluationResult];
      let bVal = b[sortBy as keyof EvaluationResult];
      
      if (sortBy === 'match') {
        aVal = a.target_label === a.pred_label ? 1 : 0;
        bVal = b.target_label === b.pred_label ? 1 : 0;
      }
      
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const getMatchStats = () => {
    const matched = allResults.filter(r => r.status === 'completed' && safeRender(r.target_label) === safeRender(r.pred_label)).length;
    const notMatched = allResults.filter(r => r.status === 'completed' && safeRender(r.target_label) !== safeRender(r.pred_label)).length;
    const failed = allResults.filter(r => r.status === 'failed').length;
    return { matched, notMatched, failed, total: matched + notMatched + failed };
  };

  const renderPieChart = () => {
    const stats = getMatchStats();
    if (stats.total === 0) return <div>No data</div>;
    
    const matchedPercent = (stats.matched / stats.total) * 100;
    const notMatchedPercent = (stats.notMatched / stats.total) * 100;
    const failedPercent = (stats.failed / stats.total) * 100;
    
    const radius = 80;
    const centerX = 100;
    const centerY = 100;
    
    const segments = [];
    
    const angle1 = (stats.matched / stats.total) * 360;
    if (angle1 > 0) segments.push({ type: 'matched', angle: angle1, color: '#28a745', count: stats.matched, percent: matchedPercent });
    
    const angle2 = (stats.notMatched / stats.total) * 360;
    if (angle2 > 0) segments.push({ type: 'not-matched', angle: angle2, color: '#dc3545', count: stats.notMatched, percent: notMatchedPercent });
    
    const angle3 = (stats.failed / stats.total) * 360;
    if (angle3 > 0) segments.push({ type: 'failed', angle: angle3, color: '#6c757d', count: stats.failed, percent: failedPercent });
    
    let startAngle = 0;
    
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '2rem', flexWrap: 'wrap', justifyContent: 'center' }}>
        <svg width="200" height="200" style={{ cursor: 'pointer', flexShrink: 0 }}>
          {segments.map((segment, index) => {
            const endAngle = startAngle + segment.angle;
            const x1 = centerX + radius * Math.cos((startAngle - 90) * Math.PI / 180);
            const y1 = centerY + radius * Math.sin((startAngle - 90) * Math.PI / 180);
            const x2 = centerX + radius * Math.cos((endAngle - 90) * Math.PI / 180);
            const y2 = centerY + radius * Math.sin((endAngle - 90) * Math.PI / 180);
            const largeArc = segment.angle > 180 ? 1 : 0;
            
            const pathData = segment.angle === 360 
              ? `M ${centerX} ${centerY} m -${radius} 0 a ${radius} ${radius} 0 1 1 ${radius * 2} 0 a ${radius} ${radius} 0 1 1 -${radius * 2} 0`
              : [
                  `M ${centerX} ${centerY}`,
                  `L ${x1} ${y1}`,
                  `A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`,
                  'Z'
                ].join(' ');
            
            const result = (
              <path
                key={index}
                d={pathData}
                fill={segment.color}
                stroke="white"
                strokeWidth="2"
                onMouseEnter={(e) => {
                  const tooltip = document.createElement('div');
                  tooltip.className = 'pie-tooltip';
                  tooltip.style.cssText = `
                    position: absolute;
                    background: rgba(0,0,0,0.8);
                    color: white;
                    padding: 0.5rem;
                    border-radius: 4px;
                    font-size: 0.8rem;
                    pointer-events: none;
                    z-index: 1000;
                  `;
                  tooltip.textContent = `${segment.type}: ${segment.count} (${segment.percent.toFixed(1)}%)`;
                  document.body.appendChild(tooltip);
                  
                  const rect = e.currentTarget.getBoundingClientRect();
                  tooltip.style.left = `${rect.left + rect.width / 2 - tooltip.offsetWidth / 2}px`;
                  tooltip.style.top = `${rect.top - tooltip.offsetHeight - 5}px`;
                }}
                onMouseLeave={() => {
                  const tooltip = document.querySelector('.pie-tooltip');
                  if (tooltip) document.body.removeChild(tooltip);
                }}
              />
            );
            
            startAngle = endAngle;
            return result;
          })}
        </svg>
        
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <div style={{ width: '12px', height: '12px', backgroundColor: '#28a745' }}></div>
            <span>Matched: {stats.matched}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <div style={{ width: '12px', height: '12px', backgroundColor: '#dc3545' }}></div>
            <span>Not Matched: {stats.notMatched}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{ width: '12px', height: '12px', backgroundColor: '#6c757d' }}></div>
            <span>Failed: {stats.failed}</span>
          </div>
        </div>
      </div>
    );
  };

  const renderConfusionMatrix = () => {
    const completedResults = allResults.filter(r => r.status === 'completed');
    if (completedResults.length === 0) return <div>No completed results</div>;
    
    const allLabels = completedResults.map(r => safeRender(r.target_label)).concat(completedResults.map(r => safeRender(r.pred_label)));
    const labels = Array.from(new Set(allLabels)).sort();
    const matrix: { [key: string]: { [key: string]: number } } = {};
    
    labels.forEach(target => {
      matrix[target] = {};
      labels.forEach(pred => {
        matrix[target][pred] = completedResults.filter(r => safeRender(r.target_label) === target && safeRender(r.pred_label) === pred).length;
      });
    });
    
    const maxCount = Math.max(...Object.values(matrix).flatMap(row => Object.values(row)));
    
    return (
      <div style={{ fontSize: '0.8rem' }}>
        <table style={{ borderCollapse: 'collapse', margin: '0 auto' }}>
          <thead>
            <tr>
              <th style={{ padding: '0.5rem', border: '1px solid #ddd', backgroundColor: '#f8f9fa' }}>Actual \ Predicted</th>
              {labels.map(label => (
                <th key={label} style={{ padding: '0.5rem', border: '1px solid #ddd', backgroundColor: '#f8f9fa', minWidth: '60px' }}>
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {labels.map(target => (
              <tr key={target}>
                <td style={{ padding: '0.5rem', border: '1px solid #ddd', backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                  {target}
                </td>
                {labels.map(pred => {
                  const count = matrix[target][pred];
                  const intensity = maxCount > 0 ? count / maxCount : 0;
                  const isCorrect = target === pred;
                  return (
                    <td
                      key={pred}
                      style={{
                        padding: '0.5rem',
                        border: '1px solid #ddd',
                        textAlign: 'center',
                        backgroundColor: isCorrect 
                          ? `rgba(40, 167, 69, ${0.2 + intensity * 0.6})` 
                          : `rgba(220, 53, 69, ${intensity * 0.4})`,
                        cursor: 'pointer'
                      }}
                      title={`Actual: ${target}, Predicted: ${pred}, Count: ${count}`}
                    >
                      {count}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  if (loading) {
    return <div>Loading evaluation results...</div>;
  }

  return (
    <div className="module-content">
      <Breadcrumb items={[
        { label: 'Home', path: '/' },
        { label: 'Projects', path: '/' },
        { label: projectId || '', path: `/project/${projectId}` },
        { label: 'Test', path: `/project/${projectId}/test` },
        { label: 'Evaluation', path: `/project/${projectId}/test/evaluation` },
        { label: evalRunId || '' }
      ]} />
      
      <h2>Evaluation Run Details</h2>
      <p>Run ID: {evalRunId}</p>
      
      {/* Visualizations */}
      <div className="evaluation-charts" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginBottom: '2rem' }}>
        {/* Pie Chart */}
        <div className="visualization-container" style={{ backgroundColor: 'white', border: '1px solid #dee2e6', borderRadius: '8px', padding: '1rem' }}>
          <h4 style={{ margin: '0 0 1rem 0' }}>Match Distribution</h4>
          {renderPieChart()}
        </div>
        
        {/* Confusion Matrix */}
        <div className="visualization-container" style={{ backgroundColor: 'white', border: '1px solid #dee2e6', borderRadius: '8px', padding: '1rem' }}>
          <h4 style={{ margin: '0 0 1rem 0' }}>Confusion Matrix</h4>
          {renderConfusionMatrix()}
        </div>
      </div>
      
      <div className="results-table evaluation-table">
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '1rem' }}>
          <thead>
            <tr style={{ backgroundColor: '#f5f5f5' }}>
              <th onClick={() => handleSort('data_point_id')} style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ddd', cursor: 'pointer', userSelect: 'none' }}>
                Data Point ID {sortField === 'data_point_id' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Input</th>
              <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Output</th>
              <th onClick={() => handleSort('target_label')} style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ddd', cursor: 'pointer', userSelect: 'none' }}>
                Target Label {sortField === 'target_label' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('pred_label')} style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ddd', cursor: 'pointer', userSelect: 'none' }}>
                Pred Label {sortField === 'pred_label' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('match')} style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #ddd', cursor: 'pointer', userSelect: 'none' }}>
                Match {sortField === 'match' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('status')} style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ddd', cursor: 'pointer', userSelect: 'none' }}>
                Status {sortField === 'status' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
            </tr>
          </thead>
          <tbody>
            {results.map((result, index) => (
              <tr key={result.data_point_id} style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f9f9f9' }}>
                <td style={{ padding: '0.75rem', borderBottom: '1px solid #eee' }}>{result.data_point_id}</td>
                <td style={{ padding: '0.75rem', borderBottom: '1px solid #eee' }}>
                  <pre style={{ fontSize: '0.8rem', maxWidth: '200px', overflow: 'auto' }}>
                    {result.input ? JSON.stringify(result.input, null, 2) : 'null'}
                  </pre>
                </td>
                <td style={{ padding: '0.75rem', borderBottom: '1px solid #eee' }}>
                  <pre style={{ fontSize: '0.8rem', maxWidth: '200px', overflow: 'auto' }}>
                    {result.output ? JSON.stringify(result.output, null, 2) : 'null'}
                  </pre>
                </td>
                <td style={{ padding: '0.75rem', borderBottom: '1px solid #eee' }}>
                  {safeRender(result.target_label)}
                </td>
                <td style={{ padding: '0.75rem', borderBottom: '1px solid #eee' }}>
                  {safeRender(result.pred_label)}
                </td>
                <td style={{ padding: '0.75rem', borderBottom: '1px solid #eee', textAlign: 'center' }}>
                  {result.status === 'failed' ? '—' : 
                   (safeRender(result.target_label) === safeRender(result.pred_label) ? 
                    <span style={{ color: '#28a745', fontSize: '1.2rem' }}>✓</span> : 
                    <span style={{ color: '#dc3545', fontSize: '1.2rem' }}>✗</span>)}
                </td>
                <td style={{ padding: '0.75rem', borderBottom: '1px solid #eee' }}>
                  <span style={{
                    padding: '0.25rem 0.5rem',
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    backgroundColor: 
                      result.status === 'completed' ? '#d4edda' :
                      result.status === 'failed' ? '#f8d7da' : '#e2e3e5',
                    color:
                      result.status === 'completed' ? '#155724' :
                      result.status === 'failed' ? '#721c24' : '#495057'
                  }}>
                    {result.status?.toUpperCase()}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem' }}>
        <button 
          onClick={handlePrevPage}
          disabled={currentPage === 1}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: currentPage === 1 ? '#f5f5f5' : '#007bff',
            color: currentPage === 1 ? '#999' : 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
          }}
        >
          Previous
        </button>
        
        <span>Page {currentPage}</span>
        
        <button 
          onClick={handleNextPage}
          disabled={!hasNextPage}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: !hasNextPage ? '#f5f5f5' : '#007bff',
            color: !hasNextPage ? '#999' : 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: !hasNextPage ? 'not-allowed' : 'pointer'
          }}
        >
          Next
        </button>
      </div>
      
      {results.length === 0 && (
        <p style={{ textAlign: 'center', color: '#666', marginTop: '2rem' }}>No results found.</p>
      )}
    </div>
  );
};

export default EvaluationDetails;